import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUMusicStudio = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-music-studio" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as a Music or Podcast Studio
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[hsl(var(--brand-primary))]">
            Soundproof. Private. Studio-Ready.
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Turn your backyard into a pro-level audio space. Whether you're a podcaster, producer, vocalist, or instrumentalist—an ADU can be custom-built for clean acoustics, recording, and rehearsal.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Music Studio Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/2b0b3af3-d756-48c3-9a1c-1bb8d5ef5a0d.png" 
                  alt="Professional studio setup with acoustic treatment"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/369ce9bc-9ad6-4240-a127-845a56fc94d7.png" 
                  alt="Sound-isolated recording space"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/3ee7f03f-a3bc-4253-87c4-f2ad9a7a4664.png" 
                  alt="Studio workspace with equipment setup"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Design Inclusions Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Professional Studio Design Inclusions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Wall + Ceiling Sound Insulation</h3>
                <p className="text-muted-foreground">
                  Professional-grade acoustic treatment to prevent sound bleed and ensure clean recordings without disturbing neighbors.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Recording Booth or Isolation Corner</h3>
                <p className="text-muted-foreground">
                  Dedicated vocal booth or instrument isolation area for pristine recording quality and separation.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Mixing Area with Cable Management</h3>
                <p className="text-muted-foreground">
                  Organized workspace for audio equipment, mixing boards, and computers with integrated cable routing systems.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Mood Lighting, Black-out Shades, HVAC Zoning</h3>
                <p className="text-muted-foreground">
                  Complete environmental control for optimal recording conditions and creative atmosphere.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Build Your Professional Studio?</h2>
          <p className="text-lg mb-12 opacity-90">
            Create without limits—and record without interruption. Let's build your studio together.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUMusicStudio;